import { useLazyQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router';
import { ErrorState, RootState } from '../../redux/AppReducer';
import { ScreenType } from '../../common/view-model';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
	mapBrandProviderAttributes,
	BrandBusinessProviderI
} from '../../components/brand-profile/model';
import { AppActions } from '../../redux/AppActions';
import { GENERAL_ERRORS, serverError } from '../../common/constants';
import './index.scss';
import BrandHeaderComponent from '../../components/brand-profile/brand-header-component/BrandHeaderComponent';
import { GoBackButtonComponent } from '../../components/UI/GoBackButtonComponent/GoBackButtonComponent';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/grid';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Pagination, Navigation, Swiper as ISwiper, Grid } from 'swiper';
import { useSelector } from 'react-redux';
import { ContactInformationComponent } from '../../components/product-detail/accordion/contactInformation/ContactInformationComponent';
import { GET_BRAND_BUSINESS_PROVIDER } from '../../graphql/queries/brandBusinessProvider';
import ImageError from '../../assets/images/no-image.png';
import { useAppDispatch } from '../../redux/store';
import { AuthActions } from '../../redux/AuthActions';
const BrandBusinessProvider: FC = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { brandId } = useParams();
	const [brand, setBrand] = useState<BrandBusinessProviderI | undefined>();
	const swiperRef = useRef<ISwiper>(null);
	const deviceScreen = useSelector((state: RootState) => state.appState?.deviceScreen);
	const currentEvent = useSelector(
		(state: RootState) => state.appState?.storeState?.getCurrentEvent
	);
	const tokenHasError = useSelector(
		({ appState: state }: RootState) => state.authState?.tokenHasError
	);
	const [getBusinessProviders, { data, loading, error }] = useLazyQuery(
		GET_BRAND_BUSINESS_PROVIDER
	);
	const handleError = (error: ErrorState) => dispatch(AppActions.setGeneralError(error));
	const handleUnauthorized = () => dispatch(AppActions.unauthorizedError());

	const handleNavigateBack = useCallback(() => {
		navigate(-1);
	}, [navigate]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		if (tokenHasError === 0 && currentEvent !== undefined) {
			getBusinessProviders({
				variables: {
					id: brandId,
					expoEventId: currentEvent?.id
				}
			});
		}
	}, [currentEvent]);

	useEffect(() => {
		if (!loading && data?.brandBusinessServiceProvider) {
			const { __typename, ...rest } = data.brandBusinessServiceProvider;
			mapBrandProviderAttributes(rest).then(brandData => setBrand(brandData));
		}
	}, [data, loading]);

	useEffect(() => {
		if (error?.message === 'Unauthorized') {
			if (tokenHasError === 0) {
				dispatch(AuthActions.setTokenError(1));
			}
		}
		if (error?.message === GENERAL_ERRORS.USER_TOKEN_EXPIRED) {
			handleUnauthorized();
		}
		if (error) {
			handleError(serverError);
		}
	}, [error]);
	return (
		<>
			{brand && (
				<div className='brand-bussiness-profile-container'>
					<GoBackButtonComponent
						handelNavigate={handleNavigateBack}
						text={'Back'}
						icon={<i className='fa fa-arrow-left' aria-hidden='true'></i>}
					/>

					<BrandHeaderComponent brand={brand} showShareMenu={false}></BrandHeaderComponent>

					{brand.offerings && brand.offerings.length > 0 && (
						<div className='container-offerings'>
							<span className='brand-info-title'>Our Offerings</span>
							<div className='container-options-offerings'>
								{brand.offerings?.map((offer, index) => (
									<div className='offering' key={`offer_${index}`}>
										<span className='title'>{offer.category}</span>
										<ul className='options'>
											{offer.offers?.map((category: any, index_cat: any) => (
												<li key={`category_${index_cat}`}>{category}</li>
											))}
										</ul>
									</div>
								))}
							</div>
						</div>
					)}
					{(brand.hasBrokerContactInformation ||
						brand.hasProdRed ||
						brand.hasContactInformation) && (
						<div className={`contact-container`}>
							<div className='brand-contact-title-container'>
								<span className='brand-contact-title'>Contact Information</span>
							</div>
							<ContactInformationComponent
								brokers={brand?.brokers}
								contact={{
									name: brand.contact_name,
									email: brand.contact_email,
									phone: brand.contact_phone
								}}
								productRep={brand.prod_rep}
								isBusinessProvider
							/>
						</div>
					)}

					{brand.clients && brand.clients.length > 0 && (
						<div className='clients-container'>
							<div className='title'>Our Clients</div>
							<div className='images-container'>
								{brand.clients?.map((client, index) => (
									<div className='image-wrapper' key={`client_image_${index}`}>
										<img className='image' src={client ? client : ImageError} alt='' />
									</div>
								))}
							</div>
						</div>
					)}

					{brand.testimonials && brand.testimonials.length > 0 && (
						<div className='text-container'>
							<span className='title'>Brand Testimonial</span>
							<div className='slider-container'>
								{deviceScreen !== ScreenType.MOBILE ? (
									<>
										<Swiper
											slidesPerView={1}
											spaceBetween={0}
											navigation={false}
											pagination={{
												clickable: true
											}}
											loop
											modules={[Pagination, Navigation]}
											onBeforeInit={swiper => {
												swiperRef.current = swiper;
											}}
										>
											{brand.testimonials?.map((testimonial, index) => (
												<SwiperSlide key={`testimonial_${index}`}>
													<div className='swiper-content'>
														<div className='description'>{`"${testimonial.description}"`}</div>
														<div className='author'>
															{`- ${testimonial.author} @ ${testimonial.company}`}
														</div>
													</div>
												</SwiperSlide>
											))}
										</Swiper>
										{brand.testimonials.length > 1 && (
											<>
												<button
													data-testid='desktop-swiper-arrow-prev'
													className='swiper-button-prev'
													onClick={() => swiperRef.current?.slidePrev()}
												></button>
												<button
													data-testid='desktop-swiper-arrow-next'
													className='swiper-button-next'
													onClick={() => swiperRef.current?.slideNext()}
												></button>
											</>
										)}
									</>
								) : (
									<>
										<Swiper
											data-testid='mobile-swiper'
											observeParents={true}
											slidesPerView={1}
											navigation={true}
											pagination={{
												el: '.swiper-custom-pagination-for-brand',
												clickable: true,
												dynamicBullets: false
											}}
											loop
											modules={[Grid, Pagination]}
											onBeforeInit={swiper => {
												swiperRef.current = swiper;
											}}
										>
											{brand.testimonials?.map((testimonial, index) => (
												<SwiperSlide key={`testimonial_${index}`}>
													<div className='swiper-content'>
														<div className='description'>{`"${testimonial.description}"`}</div>
														<div className='author'>
															{`- ${testimonial.author} @ ${testimonial.company}`}
														</div>
													</div>
												</SwiperSlide>
											))}
										</Swiper>
										{brand.testimonials.length > 1 && (
											<>
												<div data-testid='mobile-swiper-thumb'>
													<div
														className='swiper-custom-pagination-for-brand'
														style={{
															marginRight: 'auto',
															marginLeft: 'auto'
														}}
													></div>
												</div>

												<div
													data-testid='mobile-swiper-arrows'
													className='control-mobile-for-brand'
												>
													<i
														className='bi bi-chevron-left'
														onClick={() => {
															swiperRef.current?.slidePrev();
														}}
													></i>
													<i
														className='bi bi-chevron-right'
														onClick={() => {
															swiperRef.current?.slideNext();
														}}
													></i>
												</div>
											</>
										)}
									</>
								)}
							</div>
						</div>
					)}
				</div>
			)}
		</>
	);
};

export default BrandBusinessProvider;
